import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import api from "../lib/api";

const SESSION_ID = (() => {
  let s = localStorage.getItem("gdih_ai_session");
  if (!s) {
    s = "web-" + Math.random().toString(36).slice(2) + Date.now();
    localStorage.setItem("gdih_ai_session", s);
  }
  return s;
})();

const SUGGESTIONS = [
  "Best country for MBBS?",
  "Scholarships for MBA",
  "Study in USA cost",
  "Visa success tips",
];

export default function AICounsellor() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "ai",
      text: "Hi! I'm Vidya, your AI education counsellor at GDIH Elite. Ask me anything about destinations, courses, universities, scholarships or visas.",
    },
  ]);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, typing, open]);

  const send = (text) => {
    const msg = (text ?? input).trim();
    if (!msg) return;
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setInput("");
    setTyping(true);
    api
      .post("/ai/chat", { session_id: SESSION_ID, message: msg, provider: "gemini", model: "gemini-2.5-flash" })
      .then((r) => setMessages((m) => [...m, { role: "ai", text: r.data.reply }]))
      .catch(() =>
        setMessages((m) => [
          ...m,
          { role: "ai", text: "Sorry, I couldn't reach my knowledge base just now. Please try again, or book a free consult and a human counsellor will help you right away." },
        ])
      )
      .finally(() => setTyping(false));
  };

  return (
    <>
      {/* Launcher */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-[60] flex items-center gap-2 h-14 pl-4 pr-5 rounded-full bg-[#004030] text-white shadow-[0_12px_30px_-8px_rgba(0,64,48,0.6)] hover:bg-[#013d2b] transition-all hover:scale-105"
        aria-label="Open AI Counsellor"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
        {!open && <span className="font-semibold text-sm hidden sm:block">Ask Vidya AI</span>}
      </button>

      {/* Chat window */}
      <div
        className={`fixed bottom-24 right-6 z-[60] w-[calc(100vw-3rem)] sm:w-[380px] transition-all duration-300 origin-bottom-right ${
          open ? "opacity-100 scale-100 pointer-events-auto" : "opacity-0 scale-90 pointer-events-none"
        }`}
      >
        <div className="bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden flex flex-col h-[560px] max-h-[75vh]">
          {/* Header */}
          <div className="bg-[#004030] px-5 py-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#c9a227] grid place-items-center text-[#013d2b]">
              <Sparkles size={20} />
            </div>
            <div className="leading-tight">
              <p className="text-white font-semibold">Vidya — AI Counsellor</p>
              <p className="text-[#e0c05a] text-xs flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-green-400 inline-block" /> Online now
              </p>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#f7faf8]">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#004030] text-white rounded-br-md"
                      : "bg-white text-slate-700 border border-slate-100 rounded-bl-md shadow-sm"
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {typing && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-100 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                  <div className="flex gap-1">
                    {[0, 1, 2].map((d) => (
                      <span key={d} className="w-2 h-2 rounded-full bg-[#004030]/40 animate-bounce" style={{ animationDelay: `${d * 0.15}s` }} />
                    ))}
                  </div>
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {/* Suggestions */}
          {messages.length <= 1 && (
            <div className="px-4 pb-2 flex flex-wrap gap-2 bg-[#f7faf8]">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  className="text-xs px-3 py-1.5 rounded-full border border-[#004030]/25 text-[#004030] hover:bg-[#004030] hover:text-white transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="p-3 border-t border-slate-100 bg-white flex items-center gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask about courses, visas, scholarships..."
              className="flex-1 bg-[#f2f6f4] rounded-full px-4 h-11 text-sm outline-none focus:ring-2 focus:ring-[#004030]/30"
            />
            <button
              onClick={() => send()}
              className="w-11 h-11 rounded-full bg-[#c9a227] hover:bg-[#e0c05a] text-[#013d2b] grid place-items-center transition-colors shrink-0"
              aria-label="Send"
            >
              <Send size={18} />
            </button>
          </div>
          <p className="text-[10px] text-center text-slate-400 pb-2 bg-white">Powered by GDIH AI · Gemini, ChatGPT & Claude</p>
        </div>
      </div>
    </>
  );
}